import { Trans } from 'react-i18next'

import { COND_FEATURE_FLAG_ALL, ConditionFeatureFlag, IActionFormProps } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { isEmpty } from '@/shared/utils'
import { ConditionRepeaterHorizontal } from '@/components/BehaviourV2/ConditionRepeaterHorizontal'

import { IfElseFormData } from './types'

export default function ActFormIfElse({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      conditions: initialData?.conditions || {
        isAny: true,
        conditions: [],
      },
    },
  })

  const handleSubmit = onFormSubmit((data: IfElseFormData) => {
    onSubmit(data)
  })

  const handleConditionsChange = (conditions: any) => {
    form.setValues('conditions', conditions)
  }

  const currentConditions = form.getValues('conditions')

  const invalidForm =
    isEmpty(currentConditions?.conditions) || currentConditions?.conditions?.length === 0 || !!form.errors

  return (
    <div className='flex flex-col h-full md:min-w-[672px]'>
      {/* Header */}
      <div className='border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between'>
        <div>
          <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
            <Trans>Configure If/Else Condition</Trans>
          </h3>
          <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
            <Trans>Set up conditional branching for your workflow</Trans>
          </p>
        </div>
      </div>

      {/* Content */}
      <div className='flex-1 overflow-y-auto p-6'>
        <div className='space-y-6'>
          <div className='bg-green-500/10 border border-green-500/30 rounded-lg'>
            {/* TRUE Path Header */}
            <div className='p-4 border-b border-green-500/30'>
              <div className='flex items-center gap-3 mb-2'>
                <div className='flex items-center gap-2'>
                  <MaterialIcon icon='check_circle' size={24} className='text-green-500' />
                  <span className='text-lg font-semibold text-green-500'>TRUE PATH</span>
                </div>
              </div>

              <div className='rounded-md p-3 border border-green-500/30'>
                <p className='text-sm font-medium'>This path is taken when conditions below are satisfied ✓</p>
              </div>
            </div>

            {/* Conditions Section Inside TRUE Path */}
            <div className='p-4 space-y-6'>
              {/* Condition Logic */}
              <div className='space-y-4'>
                <div className='flex items-center gap-2'>
                  <h3 className='text-base font-semibold'>When should this path be taken?</h3>
                </div>
                <div className='space-y-6'>
                  <ConditionRepeaterHorizontal
                    id='if-else-conditions'
                    value={currentConditions}
                    onChange={handleConditionsChange}
                    featureFlags={COND_FEATURE_FLAG_ALL | ConditionFeatureFlag.ActionOutput}
                    actions={initialData?.actionOptions}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='p-6 pt-0'>
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold'>Otherwise (FALSE Path)</h2>

          <div className='bg-red-500/10 border border-red-500/30 rounded-lg p-4'>
            <div className='flex items-center gap-3 mb-3'>
              <div className='flex items-center gap-2'>
                <MaterialIcon icon='cancel' size={24} className='text-red-500' />
                <span className='text-lg font-semibold text-red-500'>FALSE PATH</span>
              </div>
            </div>

            <div className='rounded-md p-3 border border-red-500/20'>
              <p className='text-sm font-medium'>This path is taken when conditions are NOT satisfied</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='border-t border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center bg-gray-50 dark:bg-transparent'>
        <Button
          variant={ButtonVariant.Outlined}
          onClick={onDismiss}
          className='text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border-gray-300 dark:border-gray-600'
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save Condition</Trans>
        </Button>
      </div>
    </div>
  )
}
